import { useEffect, useState } from 'react';

/**
 * useCueMirror — mirrors the video's VTT caption cues into React state so the
 * player renders captions on ITS OWN overlay instead of the browser's
 * letterboxed cue slab (which cannot be styled to match our type — Jul 28
 * ruling: "very ugly").
 *
 * EXTRACTED VERBATIM in technique from HomeHeroVideo.js (the hard-won
 * implementation): the <track> stays in the DOM (a11y/SEO/native CC control)
 * but its mode is forced 'hidden' ('hidden' still parses the VTT and fires
 * cuechange — it only suppresses the browser's own overlay, which is the
 * point). The current line is resolved from CURRENT PLAYBACK TIME (not
 * `track.activeCues` — activeCues lags seeks), always mirrored into state;
 * the CALLER decides whether the overlay renders. If the viewer flips the
 * browser's own CC control to 'showing', we force it back to 'hidden' so our
 * rendering is the only one. Track lists populate asynchronously, hence the
 * capped retry poll.
 *
 * @param videoRef ref to the <video> element (must contain a <track> child)
 * @param active   subscribe only while true (e.g. the element is armed)
 * @returns the current cue text ('' when none)
 */
const useCueMirror = (videoRef, active = true) => {
  const [cue, setCue] = useState('');

  useEffect(() => {
    if (!active) return undefined;
    const v = videoRef.current;
    if (!v) return undefined;

    let cancelled = false;
    let detach = null;

    const attach = () => {
      if (cancelled || detach) return true; // idempotent: never double-subscribe
      const tracks = v.textTracks;
      const t = tracks && tracks[0];
      if (!t) return false;

      t.mode = 'hidden';

      const syncCue = () => {
        const cues = t.cues;
        if (!cues || cues.length === 0) { setCue(''); return; }
        const now = v.currentTime || 0;
        let text = '';
        for (let i = 0; i < cues.length; i += 1) {
          const c = cues[i];
          if (now >= c.startTime && now <= c.endTime) {
            text = text ? `${text} ${c.text}` : String(c.text || '');
          }
        }
        setCue(text.replace(/\s*\n\s*/g, ' ').trim());
      };

      const onTracksChange = () => { if (t.mode === 'showing') t.mode = 'hidden'; };

      v.addEventListener('timeupdate', syncCue);
      v.addEventListener('seeked', syncCue);
      v.addEventListener('play', syncCue);
      v.addEventListener('pause', syncCue);
      t.addEventListener('cuechange', syncCue);
      if (tracks.addEventListener) tracks.addEventListener('change', onTracksChange);
      syncCue(); // seed immediately

      detach = () => {
        v.removeEventListener('timeupdate', syncCue);
        v.removeEventListener('seeked', syncCue);
        v.removeEventListener('play', syncCue);
        v.removeEventListener('pause', syncCue);
        t.removeEventListener('cuechange', syncCue);
        if (tracks.removeEventListener) tracks.removeEventListener('change', onTracksChange);
      };
      return true;
    };

    if (attach()) {
      return () => { cancelled = true; if (detach) detach(); };
    }

    // Track list not populated yet — retry on metadata and a short capped poll.
    let attempts = 0;
    const poll = setInterval(() => {
      attempts += 1;
      if (attach() || cancelled || attempts > 40) clearInterval(poll);
    }, 150);
    const onMeta = () => { if (attach()) clearInterval(poll); };
    v.addEventListener('loadedmetadata', onMeta);
    v.addEventListener('loadeddata', onMeta);

    return () => {
      cancelled = true;
      clearInterval(poll);
      v.removeEventListener('loadedmetadata', onMeta);
      v.removeEventListener('loadeddata', onMeta);
      if (detach) detach();
    };
  }, [videoRef, active]);

  return cue;
};

export default useCueMirror;
